import type {
  AppSettings,
  EmailSenderConfig,
  UpdateAppSettingsRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface SenderTestResult {
  sent: boolean;
  dryRun: boolean;
  provider: string;
  sender: EmailSenderConfig & { name: string };
  registrySource: string;
}

export async function fetchAppSettings(
  signal?: AbortSignal,
): Promise<AppSettings> {
  const response = await fetch(`${API_BASE_URL}/admin/settings`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch app settings: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

export async function updateAppSettings(
  payload: UpdateAppSettingsRequest,
  signal?: AbortSignal,
): Promise<AppSettings> {
  const response = await fetch(`${API_BASE_URL}/admin/settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to update app settings: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Send a diagnostic email through one of the configured senders.
 *
 * Tests the **saved** registry, not the draft on screen — the comms service
 * resolves the sender exactly as a real send does, so the From address,
 * display name and BCC are all exercised. A preview that bypassed resolution
 * could pass while the real send fails.
 *
 * `to` defaults server-side to the signed-in admin, and the comms service
 * refuses any recipient outside the company domain.
 */
export async function sendSenderTestEmail(
  sender: string,
  to?: string,
  signal?: AbortSignal,
): Promise<SenderTestResult> {
  const response = await fetch(`${API_BASE_URL}/admin/email/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(to ? { sender, to } : { sender }),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Test send failed: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}
